import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, EditIcon, EmptyState, ErrorState, Field, LoadingState, Modal, Pill, useToast } from "@signet/ui";
import { ago, compact } from "@signet/web-kit";
import type { BanReason } from "../shared/lifecycle.ts";
import { BanModal } from "./accounts/status.tsx";
import { fetchReview, keys, markBanned, markNotBanned, saveCheckNote, type ReviewAccount } from "./api.ts";
import { AccountWho, CreatorChip, IgsPage, useCanDecide, useIgsCreatorPhotos } from "./shared.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Ban check: the accounts Instagram could not find on their last daily read.

   Instagram answers a banned account exactly as it answers a handle that does
   not exist, so the scanner never decides alone. A rename is followed without
   asking (by the account's Instagram id); anything else waits here. Banned
   takes it out of the live accounts and writes BANNED into the sheet. Not
   banned keeps it, and the next read tries again.

   Banned asks what kind of ban and when. The 2026-10-12 rule of appeal
   first and wait a day went on 2026-10-15: connected bans are never
   appealed, and a 3 reason ban's appeal is followed on the Accounts tab.

   Notes (Jay, 2026-10-14): whoever looks an account up writes what they saw,
   and the row then says it was checked, so two people don't check it twice.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

function NoteModal({ account, onClose, onSave }: { account: ReviewAccount | null; onClose: () => void; onSave: (note: string) => Promise<unknown> }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!account) return null;
  const text = draft ?? account.check?.note ?? "";
  const close = () => {
    setDraft(null);
    onClose();
  };
  const save = async (note: string) => {
    setBusy(true);
    try {
      await onSave(note);
      close();
    } catch {
      // The mutation's toast says what went wrong; the note stays here to try again.
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={close}
      title={`Note on @${account.handle}`}
      description="What you saw when you checked it. Saving marks the account as checked."
      footer={
        <>
          {account.check ? (
            <button type="button" className="secondary-button" disabled={busy} onClick={() => void save("")} style={{ marginRight: "auto" }}>
              Remove note
            </button>
          ) : null}
          <button type="button" className="secondary-button" onClick={close}>Cancel</button>
          <button type="button" className="button brass" disabled={!text.trim() || busy} onClick={() => void save(text)}>
            {busy ? "Saving…" : "Save note"}
          </button>
        </>
      }
    >
      <Field label="Note">
        <textarea value={text} onChange={(e) => setDraft(e.target.value)} rows={4} maxLength={1000} autoFocus placeholder="Searched the handle, page says it isn't available" />
      </Field>
    </Modal>
  );
}

function NoteCell({ a, canDecide, onOpen }: { a: ReviewAccount; canDecide: boolean; onOpen: () => void }) {
  if (!a.check) {
    return (
      <div role="cell">
        {canDecide ? (
          <button type="button" className="secondary-button tiny" onClick={onOpen}>
            <EditIcon size={11} /> Add note
          </button>
        ) : (
          <span className="faint">No note</span>
        )}
      </div>
    );
  }
  return (
    <div role="cell" className="igs-check">
      <Pill tone="good" dot>This account was checked</Pill>
      <button type="button" className="igs-check-note" onClick={canDecide ? onOpen : undefined} disabled={!canDecide} title={a.check.note}>
        {a.check.note}
      </button>
      <small>{[a.check.by, ago(a.check.at)].filter(Boolean).join(" · ")}</small>
    </div>
  );
}

export function ReviewPage() {
  const review = useQuery({ queryKey: keys.review, queryFn: fetchReview, refetchInterval: 60_000 });
  const canDecide = useCanDecide();
  const client = useQueryClient();
  const toast = useToast();
  const photos = useIgsCreatorPhotos();

  const done = () => client.invalidateQueries({ queryKey: keys.all });
  const [banning, setBanning] = useState<ReviewAccount | null>(null);
  const banned = useMutation({
    mutationFn: ({ a, ban }: { a: ReviewAccount; ban: { reason: BanReason; on: string } }) => markBanned(a.id, ban),
    onSuccess: (r, { a }) => {
      if (r.sheet === "off") toast.success(`@${a.handle} marked banned`);
      else if (r.sheet === "written") toast.success(`@${a.handle} marked banned`, "BANNED is in the sheet. The row moves to GRAVEYARD at the sheet's 3am sync.");
      else toast.error(`@${a.handle} marked banned, sheet not changed yet`, `${r.problem ?? ""} Signet tries the sheet again every ten minutes.`.trim());
      done();
    },
    onError: (error) => toast.error("Couldn't mark it banned", messageOf(error)),
  });
  const kept = useMutation({
    mutationFn: (a: ReviewAccount) => markNotBanned(a.id),
    onSuccess: (_r, a) => {
      toast.success(`@${a.handle} kept`, "It is read again at the next daily read.");
      done();
    },
    onError: (error) => toast.error("Couldn't keep it", messageOf(error)),
  });
  const [noting, setNoting] = useState<ReviewAccount | null>(null);
  const noted = useMutation({
    mutationFn: ({ a, note }: { a: ReviewAccount; note: string }) => saveCheckNote(a.id, note),
    onSuccess: (_r, { a, note }) => {
      toast.success(note.trim() ? `@${a.handle} marked checked` : `Note on @${a.handle} removed`);
      done();
    },
    onError: (error) => toast.error("Couldn't save the note", messageOf(error)),
  });
  const busy = banned.isPending || kept.isPending;
  const r = review.data;

  return (
    <IgsPage intro="Accounts Instagram could not find on their last read. Instagram gives the same answer for a banned account as for one that never existed, so a person decides. Banned writes BANNED into the sheet. Not banned keeps the account and tries again at the next read.">
      {review.isLoading ? (
        <LoadingState />
      ) : review.isError || !r ? (
        <ErrorState title="Couldn't load the ban check" onRetry={() => review.refetch()} />
      ) : (
        <>
          <section className="panel section-gap">
            <div className="panel-head">
              <div>
                <h2>Could not be found</h2>
                <p>Renamed accounts are followed automatically, so these were not found under any name.</p>
              </div>
            </div>
            <div className="panel-body flush">
              {r.accounts.length === 0 ? (
                <EmptyState icon={<CheckIcon size={18} />} title="Nothing to check" body="Every live account was found on its last read." />
              ) : (
                <div className="table-scroll">
                  <div className="table igs-review igs-centred" role="table" aria-label="Accounts to check">
                    <div className="table-row head" role="row">
                      <span role="columnheader">Account</span>
                      <span role="columnheader">Handler</span>
                      <span role="columnheader" className="num">Followers</span>
                      <span role="columnheader">Last found</span>
                      <span role="columnheader">Note</span>
                      <span role="columnheader" className="visually-hidden">Decision</span>
                    </div>
                    {r.accounts.map((a) => (
                      <div key={a.id} className="table-row" role="row">
                        <AccountWho
                          id={a.id}
                          handle={a.handle}
                          photo={a.photo}
                          sub={
                            <>
                              {a.creator ? <CreatorChip name={a.creator} photos={photos} size={13} /> : null}
                              {[a.creator ? "" : null, a.missingSince ? `not found ${ago(a.missingSince)}` : null, a.keptBefore ? `kept before, ${ago(a.keptBefore)}` : null].filter((x) => x !== null).join(" · ")}
                            </>
                          }
                        />
                        <span role="cell" className="igs-review-poster">{a.handler ?? <span className="faint">Unassigned</span>}</span>
                        <span className="tabular" role="cell">{a.followers === null ? "-" : compact(a.followers)}</span>
                        <span role="cell">{a.lastOkAt ? ago(a.lastOkAt) : <span className="faint">Never found</span>}</span>
                        <NoteCell a={a} canDecide={canDecide} onOpen={() => setNoting(a)} />
                        <div className="igs-actions" role="cell">
                          {canDecide ? (
                            <>
                              <button type="button" className="secondary-button tiny" disabled={busy} onClick={() => kept.mutate(a)}>
                                Not banned
                              </button>
                              <button type="button" className="button danger tiny" disabled={busy} onClick={() => setBanning(a)}>
                                Banned
                              </button>
                            </>
                          ) : (
                            <span className="faint">Waiting for someone with edit access</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <NoteModal account={noting} onClose={() => setNoting(null)} onSave={(note) => noted.mutateAsync({ a: noting!, note })} />
          <BanModal account={banning ? { id: banning.id, username: banning.handle } : null} onClose={() => setBanning(null)} onBan={(ban) => banned.mutateAsync({ a: banning!, ban })} />

          {r.unsent.length ? (
            <section className="panel section-gap">
              <div className="panel-head">
                <div>
                  <h2>Banned here, not in the sheet yet</h2>
                  <p>Signet tries to write BANNED into the sheet every ten minutes until it lands.</p>
                </div>
              </div>
              <div className="panel-body flush">
                <div className="table igs-unsent" role="table" aria-label="Not yet in the sheet">
                  {r.unsent.map((u) => (
                    <div key={u.id} className="table-row" role="row">
                      <strong role="cell">@{u.handle}{u.creator ? <span className="faint"> · <CreatorChip name={u.creator} photos={photos} size={14} /></span> : null}</strong>
                      <span role="cell" className="faint">{u.error ?? "Waiting for the next sync"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}
        </>
      )}
    </IgsPage>
  );
}
